export function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Check if a URL is a local blob URL that shouldn't be saved to backend.
 * Blob URLs are temporary browser-only URLs that can't be accessed externally.
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is a blob URL
 */
export function isBlobUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('blob:');
}

/**
 * Validate that media items don't contain blob URLs before saving to backend.
 * Returns an array of indices that have invalid blob URLs.
 * @param {Array} mediaItems - Array of media objects with url property
 * @returns {Array<number>} Array of indices with blob URLs
 */
export function findBlobUrlsInMedia(mediaItems) {
  if (!Array.isArray(mediaItems)) return [];
  return mediaItems
    .map((item, index) => (isBlobUrl(item?.url) ? index : -1))
    .filter(index => index !== -1);
}

/**
 * Check if any media items contain blob URLs.
 * @param {Array} mediaItems - Array of media objects with url property
 * @returns {boolean} True if any blob URLs are found
 */
export function hasBlobUrls(mediaItems) {
  return findBlobUrlsInMedia(mediaItems).length > 0;
}

/**
 * Manifest availability state for the combined label+manifest download.
 *
 * Prozo produces the manifest asynchronously after the AWB is indexed, so a
 * shipment can have `label_url` set while `manifest_url` is still NULL. That
 * window is normally seconds; when Prozo fails, it can be permanent — the
 * backend only retries the post-label manifest a couple of times. This helper
 * distinguishes the two so the UI can stop showing "Preparing documents…"
 * forever on a manifest that is never coming.
 *
 * Returns one of:
 *   - 'ready'     : both label and manifest present → download enabled
 *   - 'no-label'  : no label yet (e.g. manual shipping) → not a download state
 *   - 'preparing' : label present, manifest missing, generated recently
 *   - 'stuck'     : label present, manifest missing, and past the prepare window
 *
 * @param {object} shipment - order.shipment (may be null/undefined)
 * @param {number} [stuckAfterMs=120000] - age past which a missing manifest is
 *   treated as stuck rather than preparing. 2 min comfortably clears the
 *   backend's ~60s post-label retry budget.
 * @returns {'ready'|'no-label'|'preparing'|'stuck'}
 */
export function getManifestState(shipment, stuckAfterMs = 120000) {
  const hasLabel = !!shipment?.label_url;
  const hasManifest = !!shipment?.manifest_url;

  if (!hasLabel) return 'no-label';
  if (hasManifest) return 'ready';

  const generatedAt = shipment?.label_generated_at
    ? new Date(shipment.label_generated_at).getTime()
    : NaN;

  // Without a timestamp we can't tell fresh from stale; treat as stuck so the
  // vendor gets an actionable state (Retry manifest) rather than a perpetual
  // "Preparing…" that never resolves.
  if (Number.isNaN(generatedAt)) return 'stuck';

  return Date.now() - generatedAt <= stuckAfterMs ? 'preparing' : 'stuck';
}
